import { useState, useRef, useEffect, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import { UseKebabMenuProps, UseKebabMenuReturn } from './types';
import { VISIBILITY_NEXT_CYCLE } from '../../../utils/visibilityLabels';

/**
 * ケバブメニューの状態管理とハンドラーを提供するカスタムフック
 * 
 * @param props フックのプロパティ
 * @returns メニューの状態とハンドラー関数
 */
export const useKebabMenu = (props: UseKebabMenuProps): UseKebabMenuReturn => {
  const {
    video,
    isFavorite,
    isPrivate,
    toggleFavorite,
    showVisibilityChangeDialog,
    navigate,
    leftOffset = 0,
    onDownload,
    onDelete,
    onCopyLink,
    isCopied = false,
    isExternal = false,
  } = props;

  const currentVisibility = video.visibility || (isPrivate ? 'private' : 'public');

  // メニューの状態管理
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // メニューの開閉を切り替える関数
  const toggleMenu = useCallback((e: ReactMouseEvent): void => {
    e.stopPropagation();
    
    setIsMenuOpen(prev => {
      // メニューが開いている場合は閉じる
      if (prev) {
        return false;
      }
      
      // メニューを開く場合はポジション計算
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        // SP版（768px以下）では120px、それ以上では140px
        const menuWidth = viewportWidth <= 768 ? 120 : 140;
        
        // メニューの位置を計算（左オフセットを適用）
        let leftPosition = rect.left - leftOffset;
        
        // メニューの右端が画面の右端を超えないように調整
        if (leftPosition + menuWidth > viewportWidth) {
          leftPosition = viewportWidth - menuWidth - 10; // 10pxのマージンを確保
        }
        // 左端からもはみ出さないように
        if (leftPosition < 10) {
          leftPosition = 10;
        }
        
        setMenuPosition({
          top: rect.bottom,
          left: leftPosition
        });
      } else {
        // TODO: buttonRef.currentがnullの場合のエラーハンドリング
        console.warn('KebabMenu button ref is not available');
      }
      
      return true;
    });
  }, [leftOffset]);

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // メニュー内またはボタン内のクリックは無視
      if (
        (menuRef.current && menuRef.current.contains(target)) ||
        (buttonRef.current && buttonRef.current.contains(target))
      ) {
        return;
      }
      
      setIsMenuOpen(false);
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // スクロール時にメニューを閉じる
  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleScroll = () => {
      setIsMenuOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isMenuOpen]);

  // お気に入りメニュー項目クリック時のハンドラー
  const handleFavoriteMenuClick = useCallback((e: ReactMouseEvent): void => {
    e.stopPropagation();
    setIsMenuOpen(false);
    toggleFavorite(e);
  }, [toggleFavorite]);

  // 公開状態メニュー項目クリック時のハンドラー
  const handleVisibilityMenuClick = useCallback((
    e: ReactMouseEvent,
    selectedVisibility?: 'public' | 'private' | 'hospital_only'
  ): void => {
    e.stopPropagation();
    setIsMenuOpen(false);

    // selectedVisibilityが未指定の場合は従来挙動（1方向トグル）にフォールバック
    const fallbackVisibility = VISIBILITY_NEXT_CYCLE[currentVisibility];
    showVisibilityChangeDialog(e, selectedVisibility || fallbackVisibility);
  }, [currentVisibility, showVisibilityChangeDialog]);

  // 編集メニュー項目クリック時のハンドラー
  const handleEditMenuClick = useCallback((e: ReactMouseEvent): void => {
    e.stopPropagation();
    setIsMenuOpen(false);
    navigate(`/videos/${video.id}/edit`);
  }, [navigate, video.id]);

  // ダウンロードメニュー項目クリック時のハンドラー
  const handleDownloadMenuClick = useCallback((e: ReactMouseEvent): void => {
    e.stopPropagation();
    setIsMenuOpen(false);
    if (onDownload) {
      onDownload(e);
    }
  }, [onDownload]);

  // リンクコピーメニュー項目クリック時のハンドラー
  const handleCopyLinkMenuClick = useCallback((e: ReactMouseEvent): void => {
    e.stopPropagation();
    setIsMenuOpen(false);
    if (onCopyLink) {
      onCopyLink(e);
    }
  }, [onCopyLink]);

  // 削除メニュー項目クリック時のハンドラー
  const handleDeleteMenuClick = useCallback((e: ReactMouseEvent): void => {
    e.stopPropagation();
    setIsMenuOpen(false);
    if (onDelete) {
      onDelete(e);
    }
  }, [onDelete]);

  // メニューを閉じる関数
  const closeMenu = useCallback((): void => {
    setIsMenuOpen(false);
  }, []);

  return {
    isMenuOpen,
    menuPosition,
    menuRef,
    buttonRef,
    toggleMenu,
    closeMenu,
    handleFavoriteMenuClick,
    handleVisibilityMenuClick,
    handleEditMenuClick,
    handleDownloadMenuClick,
    handleDeleteMenuClick,
    handleCopyLinkMenuClick,
    isCopied,
    isExternal,
  };
};
